import Papa from 'papaparse';

import type { CatalogTemplateRow, VitalElement, WizardState } from './types';

/**
 * CSV column names that match the upload format expected by UploadMarkdownsDialog.
 * yadjusted_code  = override reason code
 * yadjusted_price = override price (in cents)
 */
const CSV_COLUMNS = [
  'parent_sku',
  'sku_subcode',
  'color',
  'size_code',
  'yadjusted_code',
  'yadjusted_price',
] as const;

/**
 * Builds a deduplication key from a catalog row based on which vital elements
 * the user selected. Rows sharing the same key are collapsed into one template row.
 */
function deduplicationKey(row: CatalogTemplateRow, elements: VitalElement[]): string {
  const parts: string[] = [row.parentSku ?? ''];
  if (elements.includes('colorCode')) parts.push(row.colorCode ?? '');
  if (elements.includes('sizeCode')) parts.push(row.sizeCode ?? '');
  return parts.join('\x00');
}

function deduplicateRows(
  rows: CatalogTemplateRow[],
  elements: VitalElement[],
): CatalogTemplateRow[] {
  const seen = new Map<string, CatalogTemplateRow>();
  for (const row of rows) {
    const key = deduplicationKey(row, elements);
    if (!seen.has(key)) {
      seen.set(key, row);
    }
  }
  return Array.from(seen.values());
}

function computePrice(row: CatalogTemplateRow, state: WizardState): string {
  switch (state.pricingMode) {
    case 'blank':
      return '';
    case 'resalePrice':
      return row.yerdlePrice != null ? String(row.yerdlePrice) : '';
    case 'percentOff':
      if (row.yerdlePrice == null) return '';
      return String(Math.round(row.yerdlePrice * (1 - state.percentOff / 100)));
  }
}

export interface TemplateRow {
  parent_sku: string;
  sku_subcode: string;
  color: string;
  size_code: string;
  yadjusted_code: string;
  yadjusted_price: string;
  /** Display-only in the review table; not included in the CSV. */
  color_code: string;
}

/**
 * Builds template rows from catalog data + wizard state.
 * This is split from CSV generation so the Review step can display the same rows
 * in a table before the user downloads.
 */
export function buildTemplateRows(
  catalogRows: CatalogTemplateRow[],
  state: WizardState,
): TemplateRow[] {
  const deduped = deduplicateRows(catalogRows, state.selectedVitalElements);
  const hasColor = state.selectedVitalElements.includes('colorCode');
  const hasSize = state.selectedVitalElements.includes('sizeCode');

  return deduped.map(row => ({
    parent_sku: row.parentSku ?? '',
    sku_subcode: hasColor || hasSize ? (row.skuSubcode ?? '') : '',
    color: hasColor ? (row.color ?? '') : '',
    size_code: hasSize ? (row.sizeCode ?? '') : '',
    yadjusted_code: state.reasonCode,
    yadjusted_price: computePrice(row, state),
    color_code: hasColor ? (row.colorCode ?? '') : '',
  }));
}

/**
 * Generates a CSV string ready for download. The column names match
 * the format expected by the "Upload Override Prices" upload flow.
 */
export function generateTemplateCsv(
  catalogRows: CatalogTemplateRow[],
  state: WizardState,
): string {
  const rows = buildTemplateRows(catalogRows, state);
  return Papa.unparse(rows, { columns: CSV_COLUMNS as unknown as string[] });
}
